/**
 * Assemblées — Wizard AGE typé (7 types) + AGO drafts avec preview/final + rapport gestion séparé.
 */
import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputFR } from "@/components/ui/date-input-fr";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Sparkles, Loader2, Save, Eye, CheckCircle2, ChevronRight, ChevronLeft, Download, Calendar, ScrollText, Users2 } from "lucide-react";
import { toast } from "sonner";
import LegalAlertsCard from "@/components/legal/LegalAlertsCard";
import BatchConvocationsDialog from "@/components/legal/BatchConvocationsDialog";
import { StatutsUpdateDialog } from "@/components/legal/StatutsUpdateDialog";

const formatDateFR = (iso) => {
  if (!iso) return "—";
  try { const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
};

// ============ AGE WIZARD ============
const AGE_STEPS = ["Type", "Date", "Résolution", "Participants", "Aperçu"];

function AgeWizard({ open, onOpenChange, onCreated, preset }) {
  const [step, setStep] = useState(1);
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [dateAssemblee, setDateAssemblee] = useState("");
  const [lieu, setLieu] = useState("");
  const [typedData, setTypedData] = useState({});
  const [odjEdit, setOdjEdit] = useState([]);
  const [loading, setLoading] = useState(false);
  // Wizard init (associes/dirigeants dropdowns + prefilled_typed_data)
  const [wizardCtx, setWizardCtx] = useState(null);

  useEffect(() => {
    if (open && types.length === 0) {
      api.get("/legal/age/types").then((r) => setTypes(r.data.types || [])).catch((e) => toast.error(apiError(e)));
    }
    if (!open) {
      setStep(1); setSelectedType(null); setDateAssemblee(""); setLieu(""); setTypedData({}); setOdjEdit([]); setWizardCtx(null);
    }
  }, [open]);

  const [participants, setParticipants] = useState([]);
  useEffect(() => {
    if (open) { api.get("/legal/associes").then((r) => setParticipants((r.data.associes || r.data.items || r.data || []).filter((a) => !a.date_sortie))).catch(() => setParticipants([])); }
  }, [open]);

  const chooseType = async (t, extra = {}) => {
    setSelectedType(t);
    setOdjEdit([...(t.ordre_du_jour || [])]);
    // IA-first : charge le contexte wizard (profil + dropdowns + prefilled)
    try {
      const r = await api.get(`/legal/age/wizard/init?age_type=${t.key}`);
      setWizardCtx(r.data);
      setTypedData({ ...(r.data.prefilled_typed_data || {}), ...extra });
    } catch { setWizardCtx(null); setTypedData({ ...extra }); }
    setStep(2);
  };

  useEffect(() => {
    if (!open || !preset?.age_type || types.length === 0) { return; }
    const t = types.find((x) => x.key === preset.age_type);
    if (t) {
      chooseType(t, preset.prefill || {});
      if (preset.date_assemblee) { setDateAssemblee(preset.date_assemblee); }
    }
  }, [open, preset, types]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    if (!selectedType) return "Type requis";
    if (!dateAssemblee) return "Date d'assemblée requise";
    const missing = (selectedType.fields || []).filter((f) => f.required && (typedData[f.key] === undefined || typedData[f.key] === "" || typedData[f.key] === null));
    if (missing.length > 0) return `Champs requis : ${missing.map((f) => f.label).join(", ")}`;
    return null;
  };

  const generate = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const r = await api.post("/legal/age/generate", {
        age_type: selectedType.key,
        date_assemblee: dateAssemblee,
        lieu: lieu || null,
        typed_data: typedData,
        ordre_du_jour_override: odjEdit,
      });
      toast.success("PV AGE généré par IA");
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const renderField = (f) => {
    const val = typedData[f.key] ?? "";
    const set = (v) => setTypedData({ ...typedData, [f.key]: v });
    // IA-first : dropdowns dynamiques pour cedant/cessionnaire/dirigeant
    const associesOpts = wizardCtx?.associes_options || [];
    const dirigeantsOpts = wizardCtx?.dirigeants_options || [];
    if ((f.key === "cedant" || f.key === "cessionnaire") && associesOpts.length > 0) {
      return (
        <Select value={val} onValueChange={set}>
          <SelectTrigger data-testid={`age-field-${f.key}`}><SelectValue placeholder="Choisir un associé…" /></SelectTrigger>
          <SelectContent>
            {associesOpts.map((a) => (
              <SelectItem key={a.id} value={a.label}>
                <span className="font-medium">{a.label}</span>
                <span className="text-slate-400 text-xs ml-2">— {a.nombre_parts} parts · {a.pourcentage}%</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (f.key === "dirigeant_nom" && dirigeantsOpts.length > 0) {
      return (
        <Select value={val} onValueChange={set}>
          <SelectTrigger data-testid={`age-field-${f.key}`}><SelectValue placeholder="Choisir un dirigeant…" /></SelectTrigger>
          <SelectContent>
            {dirigeantsOpts.map((d) => (
              <SelectItem key={d.id} value={d.label}>
                <span className="font-medium">{d.label}</span>
                <span className="text-slate-400 text-xs ml-2">— {d.fonction}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (f.type === "textarea") return <Textarea rows={3} value={val} onChange={(e) => set(e.target.value)} data-testid={`age-field-${f.key}`} />;
    if (f.type === "date") return <DateInputFR value={val} onChange={(e) => set(e.target.value)} data-testid={`age-field-${f.key}`} />;
    if (f.type === "number") return <Input type="number" step="0.01" value={val} onChange={(e) => set(e.target.value === "" ? "" : parseFloat(e.target.value))} data-testid={`age-field-${f.key}`} />;
    if (f.type === "boolean") return (
      <Select value={val === true ? "true" : val === false ? "false" : ""} onValueChange={(v) => set(v === "true")}>
        <SelectTrigger data-testid={`age-field-${f.key}`}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent><SelectItem value="true">Oui</SelectItem><SelectItem value="false">Non</SelectItem></SelectContent>
      </Select>
    );
    if (f.type === "select") return (
      <Select value={val} onValueChange={set}>
        <SelectTrigger data-testid={`age-field-${f.key}`}><SelectValue placeholder="Choisir..." /></SelectTrigger>
        <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
      </Select>
    );
    return <Input value={val} onChange={(e) => set(e.target.value)} data-testid={`age-field-${f.key}`} />;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 shadow-lg flex items-center justify-center text-white"><ScrollText className="w-5 h-5" /></span>
            {selectedType ? `AGE — ${selectedType.label}` : "Nouvelle AGE"}
          </SheetTitle><SheetDescription className="sr-only">Assistant AGE</SheetDescription>
          <div className="space-y-2 pt-2" data-testid="age-stepper">
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-red-500 to-orange-500 transition-[width] duration-500" style={{ width: `${((step - 1) / 4) * 100}%` }} /></div>
            <div className="flex justify-between">
              {AGE_STEPS.map((s, i) => (
                <div key={s} className="flex flex-col items-center gap-1 flex-1" data-testid={`age-step-${i + 1}`}>
                  <span className={`w-6 h-6 rounded-full text-[11px] font-semibold flex items-center justify-center ${i + 1 < step ? "bg-emerald-500 text-white" : i + 1 === step ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-500"}`}>
                    {i + 1 < step ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}</span>
                  <span className={`text-[10px] ${i + 1 === step ? "font-semibold text-slate-900" : "text-slate-500"}`}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </SheetHeader>

        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4" data-testid="age-wizard-step1">
            <p className="text-sm text-slate-600">Sélectionnez le type d'AGE à générer :</p>
            {types.map((t) => (
              <button key={t.key}
                onClick={() => chooseType(t)}
                className="w-full text-left p-3 border rounded-md hover:bg-blue-50 hover:border-blue-300 transition"
                data-testid={`age-type-${t.key}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{t.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
                    <p className="text-xs text-blue-600 mt-1">Délai convocation légal : {t.delai_convocation_jours} jours</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        )}

        {step === 2 && selectedType && (
          <div className="space-y-3 mt-4" data-testid="age-wizard-step2">
            <Badge className="bg-purple-100 text-purple-700">{selectedType.label}</Badge>
            {wizardCtx?.prefilled_typed_data && Object.keys(wizardCtx.prefilled_typed_data).length > 0 && (
              <div className="flex items-center gap-2 text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded p-2">
                <Sparkles className="w-3.5 h-3.5" />
                <span>{Object.keys(wizardCtx.prefilled_typed_data).length} champ(s) pré-rempli(s) depuis le profil société</span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date d'assemblée *</Label><DateInputFR value={dateAssemblee} onChange={(e) => setDateAssemblee(e.target.value)} data-testid="age-date" /></div>
              <div><Label>Lieu</Label><Input placeholder="Siège social" value={lieu} onChange={(e) => setLieu(e.target.value)} /></div>
            </div>
            <p className="text-xs text-blue-700">Délai légal de convocation : {selectedType.delai_convocation_jours} jours avant la date.</p>
          </div>
        )}

        {step === 3 && selectedType && (
          <div className="space-y-3 mt-4" data-testid="age-wizard-step-resolution">
            <div className="space-y-2">
                {(selectedType.fields || []).map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}{f.required ? " *" : ""}</Label>
                    {renderField(f)}
                  </div>
                ))}
            </div>
          </div>
        )}

        {step === 4 && selectedType && (
          <div className="space-y-2 mt-4" data-testid="age-wizard-step-participants">
            <p className="text-sm text-slate-600">Associés convoqués (depuis le profil société) :</p>
            {participants.length === 0 && <p className="text-sm text-amber-700">Aucun associé renseigné — ajoute-les dans Profil › Associés.</p>}
            {participants.map((a) => (
              <div key={a.id} className="flex items-center justify-between p-2 rounded border bg-white text-sm" data-testid={`age-participant-${a.id}`}>
                <span>{a.raison_sociale || `${a.prenom || ""} ${a.nom || ""}`}</span>
                <span className="font-mono text-xs text-slate-500">{a.nombre_parts || 0} parts · {a.pourcentage || 0} %</span>
              </div>
            ))}
            {participants.length > 0 && <p className="text-xs text-slate-500">Total : {participants.reduce((s, a) => s + Number(a.pourcentage || 0), 0).toLocaleString("fr-FR")} % du capital</p>}
          </div>
        )}

        {step === 5 && selectedType && (
          <div className="space-y-3 mt-4" data-testid="age-wizard-step3">
            <Card><CardContent className="p-3 space-y-1 text-sm">
              <p><strong>Type :</strong> {selectedType.label}</p>
              <p><strong>Date :</strong> {formatDateFR(dateAssemblee)}</p>
              <p><strong>Lieu :</strong> {lieu || "Siège social"}</p>
            </CardContent></Card>
            <div>
              <Label>Ordre du jour (modifiable, 1 par ligne)</Label>
              <Textarea rows={6} value={odjEdit.join("\n")} onChange={(e) => setOdjEdit(e.target.value.split("\n").filter((x) => x.trim()))} data-testid="age-odj-textarea" />
            </div>
            <Card className="bg-amber-50 border-amber-200"><CardContent className="p-3 text-xs text-amber-800">
              L'IA rédigera le PV en style juridique formel, intègrera les données typées ci-dessus et respectera le délai de convocation de {selectedType.delai_convocation_jours} jours.
            </CardContent></Card>
          </div>
        )}

        <SheetFooter className="mt-4">
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} disabled={loading}><ChevronLeft className="w-4 h-4 mr-1" />Retour</Button>}
          {step >= 2 && step <= 4 && <Button onClick={() => {
            if (step === 2 && !dateAssemblee) { toast.error("Indique la date d'assemblée"); return; }
            if (step === 3) {
              const missing = (selectedType.fields || []).filter((f) => f.required && (typedData[f.key] === undefined || typedData[f.key] === null || typedData[f.key] === ""));
              if (missing.length) { toast.error(`Champs obligatoires : ${missing.map((f) => f.label).join(", ")}`); return; }
            }
            setStep(step + 1);
          }} data-testid="age-next-btn">Suivant<ChevronRight className="w-4 h-4 ml-1" /></Button>}
          {step === 5 && (
            <Button onClick={generate} disabled={loading} data-testid="age-generate-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Générer PV avec IA
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

// ============ AGO DRAFT ============
function AgoDraftPanel({ onFinalized }) {
  const [drafts, setDrafts] = useState([]);
  const [form, setForm] = useState({
    exercice: String(new Date().getFullYear() - 1),
    date_cloture: "",
    date_assemblee: "",
    lieu: "",
    chiffre_affaires: "", resultat_net: "", resultat_exploitation: "",
    total_bilan: "", capitaux_propres: "",
    affectation_reserve_legale: "", affectation_reserves: "",
    affectation_report_nouveau: "", distribution_dividendes: "",
    quitus_dirigeants: true, renouvellement_mandats: "",
    faits_marquants: "", perspectives: "", notes: "",
  });
  const [previewContent, setPreviewContent] = useState(null);
  const [rapportContent, setRapportContent] = useState(null);
  const [loading, setLoading] = useState(null); // 'preview' | 'final' | 'rapport-preview' | 'rapport-final' | 'save'

  const loadDrafts = async () => {
    try { const r = await api.get("/legal/ago/drafts"); setDrafts(r.data.drafts || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { loadDrafts(); }, []);

  const loadDraft = async (exercice) => {
    try {
      const r = await api.get(`/legal/ago/draft/${exercice}`);
      const d = r.data;
      setForm({
        ...form,
        ...d,
        chiffre_affaires: d.chiffre_affaires ?? "",
        resultat_net: d.resultat_net ?? "",
        resultat_exploitation: d.resultat_exploitation ?? "",
        total_bilan: d.total_bilan ?? "",
        capitaux_propres: d.capitaux_propres ?? "",
        affectation_reserve_legale: d.affectation_reserve_legale ?? "",
        affectation_reserves: d.affectation_reserves ?? "",
        affectation_report_nouveau: d.affectation_report_nouveau ?? "",
        distribution_dividendes: d.distribution_dividendes ?? "",
      });
      toast.success(`Brouillon exercice ${exercice} chargé`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const buildPayload = () => {
    const p = { ...form };
    ["chiffre_affaires", "resultat_net", "resultat_exploitation", "total_bilan", "capitaux_propres",
     "affectation_reserve_legale", "affectation_reserves", "affectation_report_nouveau", "distribution_dividendes"]
      .forEach((k) => { p[k] = p[k] === "" || p[k] === null ? null : parseFloat(p[k]); });
    return p;
  };

  const saveDraft = async () => {
    if (!form.exercice) { toast.error("Exercice requis"); return; }
    setLoading("save");
    try {
      await api.post("/legal/ago/draft", buildPayload());
      toast.success("Brouillon enregistré");
      loadDrafts();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(null); }
  };

  const generatePV = async (mode) => {
    setLoading(mode);
    try {
      await api.post("/legal/ago/draft", buildPayload());
      const r = await api.post("/legal/ago/generate", { exercice: form.exercice, mode });
      if (mode === "preview") {
        setPreviewContent(r.data.content_markdown);
      } else {
        toast.success("PV AGO finalisé & archivé");
        setPreviewContent(r.data.content_markdown);
        loadDrafts();
        onFinalized?.(r.data);
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(null); }
  };

  const generateRapport = async (mode) => {
    setLoading(`rapport-${mode}`);
    try {
      await api.post("/legal/ago/draft", buildPayload());
      const r = await api.post("/legal/ago/rapport-gestion", { exercice: form.exercice, mode });
      setRapportContent(r.data.content_markdown);
      if (mode === "final") toast.success("Rapport de gestion archivé");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(null); }
  };

  return (
    <div className="space-y-4" data-testid="ago-draft-panel">
      {drafts.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Brouillons AGO existants</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {drafts.map((d) => (
              <Button key={d.id} variant="outline" size="sm"
                onClick={() => loadDraft(d.exercice)}
                data-testid={`ago-draft-${d.exercice}`}>
                <Calendar className="w-3 h-3 mr-1" />Exercice {d.exercice}
                {d.status === "finalized" && <CheckCircle2 className="w-3 h-3 ml-1 text-emerald-600" />}
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Données AGO — exercice à approuver</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Exercice *</Label><Input value={form.exercice} onChange={(e) => setForm({ ...form, exercice: e.target.value })} data-testid="ago-exercice" /></div>
            <div><Label>Date clôture</Label><DateInputFR value={form.date_cloture || ""} onChange={(e) => setForm({ ...form, date_cloture: e.target.value })} /></div>
            <div><Label>Date d'assemblée</Label><DateInputFR value={form.date_assemblee || ""} onChange={(e) => setForm({ ...form, date_assemblee: e.target.value })} /></div>
          </div>

          <p className="text-xs font-semibold text-slate-600 uppercase pt-2">Chiffres clés</p>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>CA (€)</Label><Input type="number" value={form.chiffre_affaires} onChange={(e) => setForm({ ...form, chiffre_affaires: e.target.value })} data-testid="ago-ca" /></div>
            <div><Label>Résultat exploitation</Label><Input type="number" value={form.resultat_exploitation} onChange={(e) => setForm({ ...form, resultat_exploitation: e.target.value })} /></div>
            <div><Label>Résultat net</Label><Input type="number" value={form.resultat_net} onChange={(e) => setForm({ ...form, resultat_net: e.target.value })} data-testid="ago-resultat" /></div>
            <div><Label>Total bilan</Label><Input type="number" value={form.total_bilan} onChange={(e) => setForm({ ...form, total_bilan: e.target.value })} /></div>
            <div><Label>Capitaux propres</Label><Input type="number" value={form.capitaux_propres} onChange={(e) => setForm({ ...form, capitaux_propres: e.target.value })} /></div>
          </div>

          <p className="text-xs font-semibold text-slate-600 uppercase pt-2">Affectation du résultat</p>
          <div className="grid grid-cols-4 gap-2">
            <div><Label>Réserve légale</Label><Input type="number" value={form.affectation_reserve_legale} onChange={(e) => setForm({ ...form, affectation_reserve_legale: e.target.value })} /></div>
            <div><Label>Autres réserves</Label><Input type="number" value={form.affectation_reserves} onChange={(e) => setForm({ ...form, affectation_reserves: e.target.value })} /></div>
            <div><Label>Report à nouveau</Label><Input type="number" value={form.affectation_report_nouveau} onChange={(e) => setForm({ ...form, affectation_report_nouveau: e.target.value })} /></div>
            <div><Label>Dividendes</Label><Input type="number" value={form.distribution_dividendes} onChange={(e) => setForm({ ...form, distribution_dividendes: e.target.value })} data-testid="ago-dividendes" /></div>
          </div>

          <div><Label>Faits marquants de l'exercice</Label><Textarea rows={2} value={form.faits_marquants} onChange={(e) => setForm({ ...form, faits_marquants: e.target.value })} /></div>
          <div><Label>Perspectives d'avenir</Label><Textarea rows={2} value={form.perspectives} onChange={(e) => setForm({ ...form, perspectives: e.target.value })} /></div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={saveDraft} disabled={loading === "save"} data-testid="ago-save-draft-btn">
          {loading === "save" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer le brouillon
        </Button>
        <Button variant="outline" onClick={() => generatePV("preview")} disabled={loading === "preview"} data-testid="ago-preview-btn">
          {loading === "preview" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
          Aperçu PV
        </Button>
        <Button onClick={() => generatePV("final")} disabled={loading === "final"} data-testid="ago-finalize-btn">
          {loading === "final" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Finaliser & archiver
        </Button>
        <div className="w-full h-px bg-slate-200 my-1"></div>
        <Button variant="outline" onClick={() => generateRapport("preview")} disabled={loading === "rapport-preview"} data-testid="rapport-preview-btn">
          {loading === "rapport-preview" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScrollText className="w-4 h-4 mr-2" />}
          Aperçu rapport de gestion
        </Button>
        <Button variant="outline" onClick={() => generateRapport("final")} disabled={loading === "rapport-final"} data-testid="rapport-final-btn">
          {loading === "rapport-final" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Archiver le rapport
        </Button>
      </div>

      <Dialog open={!!previewContent} onOpenChange={() => setPreviewContent(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Aperçu PV AGO — Exercice {form.exercice}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="prose prose-sm max-w-none whitespace-pre-line text-slate-800">{previewContent}</div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rapportContent} onOpenChange={() => setRapportContent(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Rapport de gestion — Exercice {form.exercice}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
          <div className="prose prose-sm max-w-none whitespace-pre-line text-slate-800">{rapportContent}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ MAIN COMPONENT ============
export default function AssembliesPanel({ onStatutsChanged, intent, onIntentConsumed, scope }) {
  const [ageTypes, setAgeTypes] = useState([]);
  useEffect(() => {
    if (scope === "age") { api.get("/legal/age/types").then((r) => setAgeTypes(r.data.types || [])).catch(() => {}); }
  }, [scope]);
  const [assemblies, setAssemblies] = useState([]);
  const [viewAssembly, setViewAssembly] = useState(null);
  const [statutsFor, setStatutsFor] = useState(null);
  const [openAgeWizard, setOpenAgeWizard] = useState(false);
  const [batchConvFor, setBatchConvFor] = useState(null);
  const [mode, setMode] = useState("liste"); // liste | age | ago
  const [filterType, setFilterType] = useState("all");

  const load = async () => {
    try { const r = await api.get("/legal/assemblies"); setAssemblies(r.data.assemblies || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const consumed = useRef(null);
  const [agePreset, setAgePreset] = useState(null);
  useEffect(() => {
    if (!intent || consumed.current === intent.nonce) { return; }
    consumed.current = intent.nonce;
    if (intent.action === "create_age") { setMode("liste"); setAgePreset(intent); setOpenAgeWizard(true); }
    if (intent.action === "create_ago") { setMode("ago"); }
    if (intent.action === "update_statuts") { setMode("liste"); toast.info("Ouvre l'AGE concernée puis clique « Mettre à jour les statuts »"); }
    onIntentConsumed?.();
  }, [intent]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = assemblies.filter((a) => (scope ? a.type === scope : filterType === "all" ? true : a.type === filterType));

  return (
    <div className="space-y-4">
      {!scope && <LegalAlertsCard />}

      {scope === "age" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="age-type-grid">
          {ageTypes.map((t) => (
            <button key={t.key} onClick={() => { setAgePreset({ age_type: t.key, prefill: {}, nonce: Date.now() }); setOpenAgeWizard(true); }}
              className="text-left p-4 rounded-xl border bg-white hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] flex gap-3" data-testid={`age-grid-${t.key}`}>
              <span className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-red-500 to-orange-600 shadow-lg flex items-center justify-center text-white"><ScrollText className="w-5 h-5" /></span>
              <span><span className="block font-medium text-sm">{t.label}</span><span className="block text-xs text-slate-500">{t.description}</span></span>
            </button>
          ))}
        </div>
      )}

      {!scope && (
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant={mode === "liste" ? "default" : "outline"} size="sm" onClick={() => setMode("liste")} data-testid="assembly-mode-liste"><FileText className="w-4 h-4 mr-2" />Liste</Button>
        <Button variant={mode === "age" ? "default" : "outline"} size="sm" onClick={() => setOpenAgeWizard(true)} data-testid="assembly-new-age"><Sparkles className="w-4 h-4 mr-2" />Nouvelle AGE typée</Button>
        <Button variant={mode === "ago" ? "default" : "outline"} size="sm" onClick={() => setMode("ago")} data-testid="assembly-new-ago"><ScrollText className="w-4 h-4 mr-2" />AGO annuelle</Button>
      </div>
      )}

      {(mode === "ago" || scope === "ago") && (
        <AgoDraftPanel onFinalized={() => load()} />
      )}

      {(mode === "liste" || scope) && (
        <>
          {scope && <p className="text-sm font-semibold pt-2">Historique des {scope.toUpperCase()}</p>}
          <div className="flex items-center gap-2">
            <p className="text-sm text-slate-600">{filtered.length} assemblée{filtered.length > 1 ? "s" : ""}</p>
            <div className="flex-1"></div>
            <Select value={filterType} onValueChange={setFilterType} disabled={!!scope}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="ago">AGO seulement</SelectItem>
                <SelectItem value="age">AGE seulement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-slate-500">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p>Aucune assemblée. Créez une AGE typée ou une AGO annuelle.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {filtered.map((a) => (
                <Card key={a.id} className="cursor-pointer hover:shadow-md transition" onClick={() => setViewAssembly(a)} data-testid={`assembly-card-${a.id}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={a.type === "ago" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>{a.type.toUpperCase()}</Badge>
                      {a.age_type_label && <Badge variant="outline" className="text-xs">{a.age_type_label}</Badge>}
                      <span className="text-sm font-medium">{formatDateFR(a.date_assemblee)}</span>
                      {a.exercice && <span className="text-xs text-slate-500">· Exercice {a.exercice}</span>}
                      {a.status === "final" && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Finalisée</Badge>}
                      {a.status === "draft" && <Badge className="bg-amber-100 text-amber-700 text-xs">Brouillon</Badge>}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{a.ordre_du_jour?.length || 0} points à l'ordre du jour</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}


      <AgeWizard open={openAgeWizard} onOpenChange={(o) => { setOpenAgeWizard(o); if (!o) { setAgePreset(null); } }} onCreated={() => load()} preset={agePreset} />
      <BatchConvocationsDialog open={!!batchConvFor} onOpenChange={() => setBatchConvFor(null)} assemblyId={batchConvFor} />

      <StatutsUpdateDialog assemblyId={statutsFor} onClose={() => setStatutsFor(null)} onChanged={onStatutsChanged} />
      <Dialog open={!!viewAssembly} onOpenChange={() => setViewAssembly(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              PV {viewAssembly?.type?.toUpperCase()}
              {viewAssembly?.age_type_label ? ` — ${viewAssembly.age_type_label}` : viewAssembly?.exercice ? ` — Exercice ${viewAssembly.exercice}` : ""}
              {" · "}{formatDateFR(viewAssembly?.date_assemblee)}
            </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end mb-2 gap-3">
            {viewAssembly && (
              <>
                {viewAssembly.type === "age" && viewAssembly.age_type !== "dissolution" && (
                  <button onClick={() => { setStatutsFor(viewAssembly.id); setViewAssembly(null); }}
                    className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline" data-testid="assembly-statuts-btn">
                    <FileText className="w-4 h-4" />Mettre à jour les statuts
                  </button>
                )}
                <button
                  onClick={() => setBatchConvFor(viewAssembly.id)}
                  className="inline-flex items-center gap-1 text-sm text-purple-600 hover:underline"
                  data-testid="assembly-batch-conv-btn">
                  <Users2 className="w-4 h-4" />Convocations groupées
                </button>
                <a href={`${api.defaults.baseURL}/legal/assemblies/${viewAssembly.id}/pdf`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                   data-testid="assembly-pdf-link">
                  <Download className="w-4 h-4" />Télécharger PDF
                </a>
              </>
            )}
          </div>
          <div className="prose prose-sm max-w-none whitespace-pre-line text-slate-800">{viewAssembly?.content_markdown}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
